// Barras semanales: lo viejo a la izquierda y la semana elegida resaltada; con menos de 4 semanas,
// tarjetas. No hay librería de gráficas en el repo: son divs, como `wk-bar` del mock.
import { cn } from '@/lib/cn';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { weekLabel } from '../../lib/payoutPeriods';

export interface WeekBar {
    periodId: string;
    cents: number;
    current: boolean;
    clawback: boolean;
}

interface WeeklyBarsProps {
    bars: readonly WeekBar[];
    selected: string | null;
    /** Sin él, las barras solo informan. */
    onSelect?: (periodId: string) => void;
    label: string;
}

const nameOf = (bar: WeekBar) => (bar.current ? 'Sem. actual' : weekLabel(bar.periodId));

export function WeeklyBars({ bars, selected, onSelect, label }: WeeklyBarsProps) {
    const max = Math.max(0, ...bars.map((bar) => bar.cents));

    if (bars.length < 4) {
        return (
            <ul className="grid grid-cols-3 gap-2" aria-label={label}>
                {bars.map((bar) => (
                    <li key={bar.periodId}>
                        <button
                            type="button"
                            disabled={!onSelect}
                            aria-pressed={bar.periodId === selected}
                            onClick={() => {
                                onSelect?.(bar.periodId);
                            }}
                            className={cn(
                                'w-full rounded-md border px-2 py-1.5 text-left',
                                bar.periodId === selected
                                    ? 'border-primary bg-primary/10'
                                    : 'border-border',
                            )}
                        >
                            <span className="block text-[10px] text-muted-foreground">
                                {nameOf(bar)}
                            </span>
                            <span className="block text-[13px] font-semibold tabular-nums">
                                {formatMoneyCents(bar.cents)}
                            </span>
                        </button>
                    </li>
                ))}
            </ul>
        );
    }

    return (
        <div>
            <ul className="flex h-[96px] items-end gap-[2px]" aria-label={label}>
                {bars.map((bar) => {
                    const title = `${nameOf(bar)} · ${formatMoneyCents(bar.cents)}`;
                    const height = max > 0 ? Math.max(2, (Math.max(bar.cents, 0) / max) * 100) : 2;
                    return (
                        <li key={bar.periodId} className="flex h-full flex-1 items-end">
                            <button
                                type="button"
                                title={title}
                                aria-label={title}
                                aria-pressed={bar.periodId === selected}
                                disabled={!onSelect}
                                onClick={() => {
                                    onSelect?.(bar.periodId);
                                }}
                                style={{ height: `${String(height)}%` }}
                                className={cn(
                                    'w-full rounded-t-sm',
                                    bar.periodId === selected
                                        ? 'bg-primary'
                                        : 'bg-primary/30 hover:bg-primary/50',
                                    bar.cents === 0 &&
                                        'border border-dashed border-border bg-transparent',
                                    bar.clawback && 'border-b-2 border-b-destructive',
                                )}
                            />
                        </li>
                    );
                })}
            </ul>
            <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
                <span>{weekLabel(bars[0].periodId)}</span>
                <span>{weekLabel(bars[bars.length - 1].periodId)}</span>
            </div>
        </div>
    );
}
